// src/services/attributes.service.js
import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  query,
  orderBy
} from "firebase/firestore";
import { db } from "./firebase/firebaseconfig";

class AttributesService {
  constructor() {
    this.colorsCollection = "product_colors";
    this.materialsCollection = "product_materials";
  }

  // ========== COLORS ==========
  async getAllColors() {
    try {
      const colorsRef = collection(db, this.colorsCollection);
      const q = query(colorsRef, orderBy("createdAt", "asc"));
      const querySnapshot = await getDocs(q);
      
      const colors = [];
      querySnapshot.forEach((doc) => {
        colors.push({
          id: doc.id,
          ...doc.data()
        });
      });

      return {
        success: true,
        colors: colors
      };
    } catch (error) {
      console.error("Get colors error:", error);
      return {
        success: false,
        error: error.message,
        colors: []
      };
    }
  }

  async addColor(colorData) {
    try {
      const colorsRef = collection(db, this.colorsCollection);
      const docRef = await addDoc(colorsRef, {
        ...colorData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      return {
        success: true,
        colorId: docRef.id,
        message: "Couleur ajoutée avec succès"
      };
    } catch (error) {
      console.error("Add color error:", error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  async updateColor(colorId, updates) {
    try {
      const colorRef = doc(db, this.colorsCollection, colorId);
      await updateDoc(colorRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });

      return {
        success: true,
        message: "Couleur modifiée avec succès"
      };
    } catch (error) {
      console.error("Update color error:", error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  async deleteColor(colorId) {
    try {
      const colorRef = doc(db, this.colorsCollection, colorId);
      await deleteDoc(colorRef);

      return {
        success: true,
        message: "Couleur supprimée avec succès"
      };
    } catch (error) {
      console.error("Delete color error:", error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // ========== MATERIALS ==========
  async getAllMaterials() {
    try {
      const materialsRef = collection(db, this.materialsCollection);
      const q = query(materialsRef, orderBy("category", "asc"));
      const querySnapshot = await getDocs(q);
      
      const materials = [];
      querySnapshot.forEach((doc) => {
        materials.push({
          id: doc.id,
          ...doc.data()
        });
      });

      return {
        success: true,
        materials: materials
      };
    } catch (error) {
      console.error("Get materials error:", error);
      return {
        success: false,
        error: error.message,
        materials: []
      };
    }
  }

  async addMaterial(materialData) {
    try {
      const materialsRef = collection(db, this.materialsCollection);
      const docRef = await addDoc(materialsRef, {
        ...materialData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      return {
        success: true,
        materialId: docRef.id,
        message: "Matériau ajouté avec succès"
      };
    } catch (error) {
      console.error("Add material error:", error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  async updateMaterial(materialId, updates) {
    try {
      const materialRef = doc(db, this.materialsCollection, materialId);
      await updateDoc(materialRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });

      return {
        success: true,
        message: "Matériau modifié avec succès"
      };
    } catch (error) {
      console.error("Update material error:", error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  async deleteMaterial(materialId) {
    try {
      const materialRef = doc(db, this.materialsCollection, materialId);
      await deleteDoc(materialRef);

      return {
        success: true,
        message: "Matériau supprimé avec succès"
      };
    } catch (error) {
      console.error("Delete material error:", error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // ========== SEED DATA (Run once to populate) ==========
  async seedColors() {
    const defaultColors = [
      { value: 'Noir', label: 'Noir', hex: '#000000' },
      { value: 'Blanc', label: 'Blanc', hex: '#FFFFFF' },
      { value: 'Gris', label: 'Gris', hex: '#808080' },
      { value: 'Beige', label: 'Beige', hex: '#F5F5DC' },
      { value: 'Marron', label: 'Marron', hex: '#8B4513' },
      { value: 'Bois Naturel', label: 'Bois Naturel', hex: '#DEB887' },
      { value: 'Chêne', label: 'Chêne', hex: '#C19A6B' },
      { value: 'Noyer', label: 'Noyer', hex: '#5C4033' },
      { value: 'Acajou', label: 'Acajou', hex: '#C04000' },
      { value: 'Bleu', label: 'Bleu', hex: '#0066CC' },
      { value: 'Rouge', label: 'Rouge', hex: '#CC0000' },
      { value: 'Vert', label: 'Vert', hex: '#00AA00' },
      { value: 'Argent', label: 'Argent', hex: '#C0C0C0' },
      { value: 'Or', label: 'Or', hex: '#FFD700' },
      { value: 'Anthracite', label: 'Anthracite', hex: '#293133' }
    ];

    try {
      for (const color of defaultColors) {
        await this.addColor(color);
      }
      return { success: true, message: 'Couleurs ajoutées' };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async seedMaterials() {
    const defaultMaterials = [
      { value: 'Bois Massif', label: 'Bois Massif', category: 'Bois' },
      { value: 'MDF', label: 'MDF (Panneau de fibres)', category: 'Bois' },
      { value: 'Contreplaqué', label: 'Contreplaqué', category: 'Bois' },
      { value: 'Aggloméré', label: 'Aggloméré', category: 'Bois' },
      { value: 'Bois de Chêne', label: 'Bois de Chêne', category: 'Bois' },
      { value: 'Bois de Pin', label: 'Bois de Pin', category: 'Bois' },
      { value: 'Bois de Hêtre', label: 'Bois de Hêtre', category: 'Bois' },
      { value: 'Acier', label: 'Acier', category: 'Métal' },
      { value: 'Aluminium', label: 'Aluminium', category: 'Métal' },
      { value: 'Métal Chromé', label: 'Métal Chromé', category: 'Métal' },
      { value: 'Fer Forgé', label: 'Fer Forgé', category: 'Métal' },
      { value: 'Inox', label: 'Inox (Acier inoxydable)', category: 'Métal' },
      { value: 'Verre Trempé', label: 'Verre Trempé', category: 'Verre' },
      { value: 'Verre', label: 'Verre', category: 'Verre' },
      { value: 'Cuir', label: 'Cuir', category: 'Tissus' },
      { value: 'Simili Cuir', label: 'Simili Cuir', category: 'Tissus' },
      { value: 'Tissu', label: 'Tissu', category: 'Tissus' },
      { value: 'Velours', label: 'Velours', category: 'Tissus' },
      { value: 'Plastique', label: 'Plastique', category: 'Synthétique' },
      { value: 'Mélaminé', label: 'Mélaminé', category: 'Synthétique' },
      { value: 'Résine', label: 'Résine', category: 'Synthétique' },
      { value: 'Stratifié', label: 'Stratifié', category: 'Synthétique' }
    ];

    try {
      for (const material of defaultMaterials) {
        await this.addMaterial(material);
      }
      return { success: true, message: 'Matériaux ajoutés' };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }
}

export default new AttributesService();